// Pantalla de proyectos: todos, agrupados por estado.
//
// Antes era una lista sola por prioridad, con un botón aparte para "ver
// archivados". Con once proyectos eso se leía como una bolsa: para saber qué
// estaba en producción había que recorrerla entera. Ahora cada estado es una
// tarjeta plegable —en desarrollo, en producción, de guardia, ideas, pausados,
// archivados— y el archivo dejó de ser otra pantalla: es el último grupo,
// cerrado.
//
// Y desde el archivo se vuelve: el botón "Sacar del archivo" deshace la baja
// lógica sin pasar por la ficha.

import { api } from "../api";
import { esc } from "../html";
import { iconoMas, iconoFlechaDer, iconoTacho } from "../iconos";
import {
  atributoColor,
  selectorColor,
  ETIQUETA_ESTADO_PROYECTO,
  clasePrioridad,
} from "../etiquetas";
import { ESTADOS_PROYECTO, PRIORIDADES } from "../../lib/tipos";
import type {
  Cliente,
  ColorProyecto,
  EstadoProyecto,
  ProyectoEnPapelera,
  ProyectoListado,
} from "../../lib/tipos";
import { formatoLocal } from "../../lib/fecha";

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/**
 * En qué orden se muestran los grupos: primero lo que está en movimiento.
 *
 * No es el orden del ciclo de vida (idea → desarrollo → producción): es el
 * orden en que a uno le sirve encontrarlos. Lo que está andando arriba, las
 * ideas y lo dormido abajo.
 *
 * De guardia va pegado a producción porque son la misma familia —lo que está
 * vivo— y se los busca juntos. Que arranque cerrado no es un descuido: es todo
 * el punto del estado, que el proyecto sano deje de ocupar pantalla.
 */
const ORDEN_GRUPOS: readonly EstadoProyecto[] = [
  "EN_DESARROLLO",
  "EN_PRODUCCION",
  "DE_GUARDIA",
  "IDEA",
  "PAUSADO",
  "ARCHIVADO",
];

/** Los únicos que arrancan abiertos: lo que se está laburando hoy. */
const ABIERTOS_DE_FABRICA: readonly EstadoProyecto[] = ["EN_DESARROLLO", "EN_PRODUCCION"];

/**
 * Una tarjeta de proyecto, montada sobre el deslizador.
 *
 * Abajo y a la derecha va el fondo rojo con el tacho, quieto. Arriba va la
 * tarjeta de siempre, opaca, que es la que se corre con el dedo y lo destapa.
 * El boton rojo queda fuera del tabulador (`tabindex="-1"`): con teclado o
 * lector de pantalla el camino es el boton de la ficha, no un gesto.
 *
 * OJO con `opacity-60`, el apagado de los pausados y archivados: va en el
 * deslizador de afuera, NO en la tarjeta. Puesto en la tarjeta la vuelve
 * translucida —la opacidad no atenua solo el texto, atenua el fondo— y el rojo
 * de abajo se transparenta: la tarjeta parece ya deslizada y ademas se queda
 * con el toque, asi que el boton no responde. Puesto afuera, el grupo entero se
 * ve tenue pero la tarjeta sigue siendo opaca respecto del boton y lo tapa.
 */
function tarjeta(p: ProyectoListado): string {
  const apagado = p.estado === "PAUSADO" || p.estado === "ARCHIVADO" ? "opacity-60" : "";
  return `
    <div class="deslizador ${apagado}">
      <button type="button" class="accion-tacho bTirar" tabindex="-1"
              data-id="${p.id}" data-nombre="${esc(p.nombre)}"
              aria-label="Tirar ${esc(p.nombre)} al tacho">
        ${iconoTacho} Tirar
      </button>
    <div class="fila color-marca deslizable"${atributoColor(p.color)}>
      <a href="#/proyecto/${p.id}" class="block p-3">
        <div class="min-w-0">
          <div class="truncate font-medium">${esc(p.nombre)}</div>
          <div class="mt-0.5 truncate text-xs text-apagado">
            ${p.cliente_nombre ? esc(p.cliente_nombre) : "proyecto propio"}
          </div>
        </div>
        <div class="mt-2 flex flex-wrap items-center gap-1.5">
          <span class="${clasePrioridad(p.prioridad)}">P${p.prioridad}</span>
          ${
            p.pendientes
              ? `<span class="chip">${plural(p.pendientes, "pendiente", "pendientes")}</span>`
              : `<span class="chip chip-ok">al día</span>`
          }
          ${
            p.bloqueadas
              ? `<span class="chip chip-alerta">${plural(p.bloqueadas, "bloqueada", "bloqueadas")}</span>`
              : ""
          }
        </div>
      </a>
      ${
        p.activo === 0
          ? `<div class="flex items-center gap-2 border-t border-borde px-3 py-2">
               <button type="button" class="bDesarchivar btn-sec px-3 py-1.5 text-sm"
                       data-id="${p.id}">Sacar del archivo</button>
               <span class="text-xs text-apagado">vuelve como pausado</span>
             </div>`
          : ""
      }
    </div>
    </div>`;
}

/**
 * El tacho, abajo de todo y cerrado. No es un grupo de estado —lo de adentro
 * conserva el suyo— asi que no pasa por grupo(): tiene su propia forma, con lo
 * que cada proyecto se lleva puesto si se vacia, y el aviso de cual no se va a
 * poder borrar.
 */
function grupoTacho(enElTacho: ProyectoEnPapelera[]): string {
  return `
    <details class="grupo" data-estado="__TACHO__">
      <summary class="grupo-cab">
        <span class="min-w-0 flex-1">
          <span class="block truncate font-semibold">Tacho de basura</span>
          <span class="mt-1.5 flex flex-wrap gap-1.5">
            <span class="chip">${plural(enElTacho.length, "proyecto", "proyectos")}</span>
          </span>
        </span>
        <span class="chevron-grupo shrink-0 text-apagado">${iconoFlechaDer}</span>
      </summary>
      <div class="space-y-2 border-t border-realce p-2">
        ${enElTacho.map(filaTacho).join("")}
        <p id="avisoTacho" class="px-1 text-sm text-alerta"></p>
        <button type="button" id="bVaciar" class="btn-peligro w-full">
          ${iconoTacho} Vaciar el tacho
        </button>
        <p class="px-1 text-xs text-apagado">
          Vaciar borra de verdad: los proyectos, sus tareas, sus notas y sus recordatorios.
          De acá no se vuelve.
        </p>
      </div>
    </details>`;
}

function filaTacho(p: ProyectoEnPapelera): string {
  const cuenta = [
    p.tareas ? plural(p.tareas, "tarea", "tareas") : "",
    p.notas ? plural(p.notas, "nota", "notas") : "",
  ].filter(Boolean);
  return `
    <div class="fila color-marca p-3 opacity-70"${atributoColor(p.color)}>
      <div class="min-w-0">
        <div class="truncate font-medium">${esc(p.nombre)}</div>
        <div class="mt-0.5 text-xs text-apagado">
          tirado el ${formatoLocal(p.papelera_el.slice(0, 10))}${
            cuenta.length ? " · se lleva " + cuenta.join(" y ") : ""
          }
        </div>
      </div>
      ${
        p.motivo_frena
          ? `<p class="mt-2 text-xs text-alerta">No se va a borrar: ${esc(p.motivo_frena)}.</p>`
          : ""
      }
      <div class="mt-2">
        <button type="button" class="bSacarTacho btn-sec px-3 py-1.5 text-sm" data-id="${p.id}">
          Sacar del tacho
        </button>
      </div>
    </div>`;
}

/**
 * Un grupo de estado. Es un <details>, igual que las tarjetas de la pantalla
 * Hoy: el plegado lo hace el navegador y el encabezado dice lo que hay adentro
 * sin abrirlo.
 */
function grupo(estado: EstadoProyecto, proyectos: ProyectoListado[], abierto: boolean): string {
  const pendientes = proyectos.reduce((n, p) => n + p.pendientes, 0);
  const bloqueadas = proyectos.reduce((n, p) => n + p.bloqueadas, 0);
  // En el archivo los conteos de tareas no significan nada: son de proyectos
  // que nadie está mirando. Sólo va cuántos hay.
  const cuenta = estado === "ARCHIVADO" ? 0 : pendientes;
  return `
    <details class="grupo" data-estado="${estado}" ${abierto ? "open" : ""}>
      <summary class="grupo-cab">
        <span class="min-w-0 flex-1">
          <span class="block truncate font-semibold">${ETIQUETA_ESTADO_PROYECTO[estado]}</span>
          <span class="mt-1.5 flex flex-wrap gap-1.5">
            <span class="chip">${plural(proyectos.length, "proyecto", "proyectos")}</span>
            ${cuenta ? `<span class="chip">${plural(cuenta, "pendiente", "pendientes")}</span>` : ""}
            ${
              estado !== "ARCHIVADO" && bloqueadas
                ? `<span class="chip chip-alerta">${plural(bloqueadas, "bloqueada", "bloqueadas")}</span>`
                : ""
            }
          </span>
        </span>
        <span class="chevron-grupo shrink-0 text-apagado">${iconoFlechaDer}</span>
      </summary>
      <div class="space-y-2 border-t border-realce p-2">
        ${proyectos.map(tarjeta).join("")}
      </div>
    </details>`;
}

function formularioNuevo(clientes: Cliente[]): string {
  return `
    <details id="nuevo" class="tarjeta">
      <summary class="flex items-center gap-2 font-medium text-acento">
        ${iconoMas} Proyecto nuevo
      </summary>
      <form id="fNuevo" class="mt-4 space-y-3">
        <label><span class="etiqueta">Nombre</span>
          <input id="nombre" class="campo" required maxlength="120" /></label>
        <div class="grid grid-cols-2 gap-3">
          <label><span class="etiqueta">Estado</span>
            <select id="estado" class="campo">
              ${ESTADOS_PROYECTO.filter((e) => e !== "ARCHIVADO")
                .map((e) => `<option value="${e}">${ETIQUETA_ESTADO_PROYECTO[e]}</option>`)
                .join("")}
            </select></label>
          <label><span class="etiqueta">Prioridad</span>
            <select id="prioridad" class="campo">
              ${PRIORIDADES
                .map((n) => `<option value="${n}"${n === 3 ? " selected" : ""}>P${n}</option>`)
                .join("")}
            </select></label>
        </div>
        <label><span class="etiqueta">Cliente</span>
          <select id="cliente" class="campo">
            <option value="">Proyecto propio</option>
            ${clientes.map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`).join("")}
            <option value="__nuevo">+ Cliente nuevo…</option>
          </select></label>
        <label id="lblClienteNuevo" class="hidden"><span class="etiqueta">Nombre del cliente</span>
          <input id="clienteNuevo" class="campo" maxlength="120" /></label>
        <div>
          <span class="etiqueta">Color</span>
          ${selectorColor(null)}
        </div>
        <p id="errNuevo" class="text-sm text-alerta"></p>
        <button class="btn-primario w-full">Crear</button>
      </form>
    </details>`;
}

export async function renderProyectos(contenedor: HTMLElement): Promise<void> {
  // Qué grupos abrió o cerró a mano. Sobrevive a los repintados de crear o
  // desarchivar; se pierde al salir de la pantalla.
  const plegados = new Map<string, boolean>();

  async function pintar(): Promise<void> {
    const [proyectos, clientes, enElTacho] = await Promise.all([
      api.get<ProyectoListado[]>("/api/proyectos?todos=1"),
      api.get<Cliente[]>("/api/clientes"),
      api.get<ProyectoEnPapelera[]>("/api/proyectos/papelera"),
    ]);

    const activos = proyectos.filter((p) => p.activo === 1);
    const archivados = proyectos.length - activos.length;

    // Un grupo por estado, salteando los vacíos: un "En producción (0)" sólo
    // ocupa lugar.
    const grupos = ORDEN_GRUPOS.map(
      (estado) => [estado, proyectos.filter((p) => p.estado === estado)] as const,
    ).filter(([, ps]) => ps.length > 0);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Proyectos</h1>
      <p class="subtitulo">${plural(activos.length, "activo", "activos")}${
        archivados ? ` · ${archivados} en el archivo` : ""
      }${enElTacho.length ? ` · ${enElTacho.length} en el tacho` : ""}</p>

      <div class="mt-4 space-y-2.5">
        ${
          grupos.length
            ? grupos
                .map(([estado, ps]) =>
                  grupo(estado, ps, plegados.get(estado) ?? ABIERTOS_DE_FABRICA.includes(estado)),
                )
                .join("")
            : `<div class="tarjeta text-center text-apagado">
                 Todavía no hay proyectos. Creá el primero acá abajo.
               </div>`
        }
        ${enElTacho.length ? grupoTacho(enElTacho) : ""}
      </div>

      <div class="mt-4">${formularioNuevo(clientes)}</div>`;

    // Que el plegado sobreviva al repintado.
    contenedor.querySelectorAll<HTMLDetailsElement>("details[data-estado]").forEach((d) => {
      d.addEventListener("toggle", () => {
        if (d.dataset.estado) plegados.set(d.dataset.estado, d.open);
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bDesarchivar").forEach((b) => {
      b.addEventListener("click", async () => {
        if (b.disabled) return;
        b.disabled = true;
        try {
          // Vuelve como PAUSADO: al archivar se pisó el estado que tenía, así
          // que no hay a cuál volver. Desde la ficha se cambia en un toque.
          await api.post(`/api/proyectos/${b.dataset.id}/restaurar`, { estado: "PAUSADO" });
          plegados.set("PAUSADO", true); // que lo vea aparecer donde cayó
          await pintar();
        } catch {
          b.disabled = false;
        }
      });
    });

    // -----------------------------------------------------------------
    // Arrastrar la tarjeta hacia la izquierda para tirarla al tacho.
    //
    // Se escribe a mano con Pointer Events, sin libreria: son treinta lineas y
    // cualquier paquete de gestos pesa mas que todo el bundle de esta pantalla.
    // Como es Pointer y no Touch, el arrastre con el mouse tambien anda.
    //
    // Lo importante es no pelearse con el scroll: hasta que el dedo no se movio
    // 8 px no se decide nada, y ahi se mira si el movimiento fue mas horizontal
    // que vertical. Si fue vertical, la tarjeta no se mueve y la pagina scrollea
    // como siempre.
    // -----------------------------------------------------------------
    const CORRIMIENTO = 96; // el ancho del boton rojo, w-24
    // Que tarjeta esta corrida lo dice la clase `abierto`, no una variable: dos
    // lugares para el mismo dato se desincronizan, y ahi el arrastre empieza a
    // calcular desde donde no esta. La unica lista que se guarda es "cerra todo
    // lo que este abierto", que se resuelve preguntandole al DOM.
    const cerrarAbierta = (): void =>
      contenedor
        .querySelectorAll<HTMLElement>(".deslizable.abierto")
        .forEach((t) => t.classList.remove("abierto"));

    contenedor.querySelectorAll<HTMLElement>(".deslizable").forEach((tarjeta) => {
      let x0 = 0;
      let y0 = 0;
      let dx = 0;
      let eje: "horizontal" | "vertical" | null = null;
      let apoyado = false;

      tarjeta.addEventListener("pointerdown", (ev) => {
        if (ev.pointerType === "mouse" && ev.button !== 0) return;
        x0 = ev.clientX;
        y0 = ev.clientY;
        dx = 0;
        eje = null;
        apoyado = true;
        tarjeta.classList.add("sin-transicion");
      });

      tarjeta.addEventListener("pointermove", (ev) => {
        if (!apoyado) return;
        dx = ev.clientX - x0;
        const dy = ev.clientY - y0;
        if (!eje) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          eje = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
          // Capturar el puntero: si el dedo se sale de la tarjeta a mitad del
          // arrastre, los eventos siguen llegando y no queda a medio camino.
          // Si el navegador no deja capturar, el gesto igual anda mientras el
          // dedo no se salga: es una mejora, no un requisito, y no puede ser lo
          // que rompa el arrastre entero.
          if (eje === "horizontal") {
            try {
              tarjeta.setPointerCapture(ev.pointerId);
            } catch {
              /* sin captura, pero arrastrando igual */
            }
          }
        }
        if (eje !== "horizontal") return;
        // Solo a la izquierda, y no mas alla del boton que hay abajo.
        const desde = tarjeta.classList.contains("abierto") ? -CORRIMIENTO : 0;
        tarjeta.style.transform = `translateX(${Math.max(-CORRIMIENTO, Math.min(0, desde + dx))}px)`;
      });

      const soltar = (): void => {
        if (!apoyado) return;
        apoyado = false;
        tarjeta.classList.remove("sin-transicion");
        tarjeta.style.transform = ""; // de aca en mas manda la clase
        if (eje !== "horizontal") return;
        const desde = tarjeta.classList.contains("abierto") ? -CORRIMIENTO : 0;
        const quedaAbierta = desde + dx < -CORRIMIENTO / 2;
        cerrarAbierta();
        if (quedaAbierta) tarjeta.classList.add("abierto");
      };
      tarjeta.addEventListener("pointerup", soltar);
      tarjeta.addEventListener("pointercancel", soltar);

      // Con la tarjeta corrida —o recien arrastrada— el toque cierra en vez de
      // abrir el proyecto. Va en captura para ganarle al <a>.
      tarjeta.addEventListener(
        "click",
        (ev) => {
          if (eje === "horizontal" || tarjeta.classList.contains("abierto")) {
            ev.preventDefault();
            ev.stopPropagation();
            cerrarAbierta();
          }
        },
        true,
      );
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bTirar").forEach((b) => {
      b.addEventListener("click", async () => {
        if (b.disabled) return;
        b.disabled = true;
        try {
          // Sin preguntar: para eso esta el tacho. Lo reversible no se confirma.
          await api.post(`/api/proyectos/${b.dataset.id}/papelera`, {});
          await pintar();
        } catch {
          b.disabled = false;
        }
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bSacarTacho").forEach((b) => {
      b.addEventListener("click", async () => {
        if (b.disabled) return;
        b.disabled = true;
        try {
          // Sin mandar estado: del tacho vuelve con el que tenia.
          const r = await api.post<{ estado?: string }>(
            `/api/proyectos/${b.dataset.id}/restaurar`,
            {},
          );
          if (r.estado) plegados.set(r.estado, true); // que lo vea volver a su grupo
          await pintar();
        } catch {
          b.disabled = false;
        }
      });
    });

    // Vaciar: el unico borrado de verdad de la app, y el unico lugar donde se
    // pregunta dos veces. La primera dice que se lleva puesto; la segunda, que
    // no hay vuelta. Los que no se van a poder borrar se descuentan de la
    // cuenta y se avisan aparte: prometer que se borran seria mentir.
    contenedor.querySelector("#bVaciar")?.addEventListener("click", async () => {
      const aviso = contenedor.querySelector("#avisoTacho") as HTMLElement;
      aviso.textContent = "";
      const seVan = enElTacho.filter((p) => !p.motivo_frena);
      const frenados = enElTacho.filter((p) => p.motivo_frena);

      if (!seVan.length) {
        aviso.textContent =
          "No hay nada que se pueda borrar: lo que está en el tacho tiene presupuestos o contratos.";
        return;
      }

      const tareas = seVan.reduce((n, p) => n + p.tareas, 0);
      const notas = seVan.reduce((n, p) => n + p.notas, 0);
      const arrastra = [
        tareas ? plural(tareas, "tarea", "tareas") : "",
        notas ? plural(notas, "nota", "notas") : "",
      ].filter(Boolean);

      if (
        !confirm(
          `¿Vaciar el tacho?\n\nSe borra${seVan.length === 1 ? "" : "n"} ${plural(seVan.length, "proyecto", "proyectos")}` +
            `${arrastra.length ? ", con " + arrastra.join(" y ") : ""}.` +
            (frenados.length
              ? `\n\n${plural(frenados.length, "proyecto queda", "proyectos quedan")} en el tacho por tener presupuestos o contratos.`
              : ""),
        )
      ) {
        return;
      }
      if (!confirm("Última pregunta: no hay de dónde traerlos de vuelta.\n\n¿Seguro?")) return;

      try {
        const r = await api.del<{ borrados: number; quedaron: { nombre: string; motivo: string }[] }>(
          "/api/proyectos/papelera",
        );
        await pintar();
        if (r.quedaron.length) {
          const nuevoAviso = contenedor.querySelector("#avisoTacho") as HTMLElement | null;
          if (nuevoAviso) {
            nuevoAviso.textContent = r.quedaron
              .map((q) => `«${q.nombre}» quedó: ${q.motivo}.`)
              .join(" ");
          }
        }
      } catch (e) {
        aviso.textContent = e instanceof Error ? e.message : "No se pudo vaciar";
      }
    });

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement | null;
    if (!form) return;

    const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
    const lblNuevo = contenedor.querySelector("#lblClienteNuevo") as HTMLElement;
    selCliente.addEventListener("change", () => {
      lblNuevo.classList.toggle("hidden", selCliente.value !== "__nuevo");
    });

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      const nombre = (contenedor.querySelector("#nombre") as HTMLInputElement).value.trim();
      if (!nombre) return;

      try {
        // Cliente nuevo al vuelo: se crea antes y se usa su id. La ficha
        // completa del cliente es de la fase 3.
        let clienteId: number | null = null;
        if (selCliente.value === "__nuevo") {
          const nombreCliente = (
            contenedor.querySelector("#clienteNuevo") as HTMLInputElement
          ).value.trim();
          if (!nombreCliente) {
            err.textContent = "Falta el nombre del cliente";
            return;
          }
          const creado = await api.post<Cliente>("/api/clientes", { nombre: nombreCliente });
          clienteId = creado.id;
        } else if (selCliente.value) {
          clienteId = Number(selCliente.value);
        }

        const estado = (contenedor.querySelector("#estado") as HTMLSelectElement)
          .value as EstadoProyecto;
        const color = String(new FormData(form).get("color") ?? "");
        await api.post<{ id: number }>("/api/proyectos", {
          nombre,
          cliente_id: clienteId,
          estado,
          prioridad: Number((contenedor.querySelector("#prioridad") as HTMLSelectElement).value),
          color: (color || null) as ColorProyecto | null,
        });
        plegados.set(estado, true); // que el proyecto recién creado se vea
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los proyectos: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
